// Client-side reel renderer using ffmpeg.wasm.
//
// Why this exists: the backend FFmpeg renderer occasionally OOM-kills on
// smaller deploy pods when a reel mixes user-uploaded videos with
// AI-generated images. Rendering on the user's device removes that
// infrastructure dependency entirely — the only failure modes are now
// the user's browser/device, which we can detect and surface clearly.
//
// Layout matches /app/backend/server.py::_compose_scene_frame as closely as
// possible (480×854 canvas, gradient overlays, brand badge, headline +
// subtitle + CTA pill). Fonts can't match exactly without bundling the
// DejaVu .ttf, so we use a system-bold-sans-serif stack that's visually
// close.

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { api } from "./api";

export const VIDEO_W = 480;
export const VIDEO_H = 854;

// Single-threaded core — works without COOP/COEP headers, which we don't
// control on the Emergent ingress. Multi-thread would be ~2× faster but
// requires `Cross-Origin-Embedder-Policy: require-corp`.
const FFMPEG_CORE_VERSION = "0.12.6";
const FFMPEG_CORE_BASE = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

let _ffmpegInstance = null;
let _ffmpegLoading = null;

async function getFFmpeg(onMessage) {
  if (_ffmpegInstance) return _ffmpegInstance;
  if (_ffmpegLoading) return _ffmpegLoading;
  _ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg();
    if (onMessage) {
      ffmpeg.on("log", ({ message }) => onMessage(message));
    }
    await ffmpeg.load({
      coreURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, "application/wasm"),
    });
    _ffmpegInstance = ffmpeg;
    return ffmpeg;
  })();
  return _ffmpegLoading;
}

// ============== text utilities ==============

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const w of words) {
    const trial = current ? current + " " + w : w;
    if (ctx.measureText(trial).width <= maxWidth) {
      current = trial;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// Iteratively shrink the font until every wrapped line fits in `maxWidth`
// AND the total text block height fits in `maxHeight`. Returns the chosen
// fontSize and the resulting wrapped lines. Without this, a single long
// word like "RISTRUTTURAZIONI" or "PROFESSIONISTA" overflows the canvas
// because wrapText() only breaks on whitespace.
function fitTextBlock(ctx, fontWeight, fontFamily, text, maxWidth, maxHeight, startFontSize, minFontSize = 18, lineGapRatio = 1.09) {
  // Defensive: empty text → return a degenerate but safe result.
  if (!text || !text.trim()) {
    return { fontSize: startFontSize, lines: [], lineH: startFontSize, totalH: 0 };
  }
  let size = startFontSize;
  while (size >= minFontSize) {
    ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
    const lines = wrapText(ctx, text, maxWidth);
    // Edge case: if the chosen font is unavailable, measureText() can
    // return 0 for every glyph → wrapText collapses everything into one
    // unbroken line which still measures 0 → loop "fits" instantly but
    // the canvas paints nothing. Detect this and force a fallback.
    if (lines.length === 0) {
      size -= 2;
      continue;
    }
    const widestLine = Math.max(...lines.map((ln) => ctx.measureText(ln).width));
    const lineH = Math.round(size * lineGapRatio);
    const totalH = lineH * lines.length;
    if (widestLine > 0 && widestLine <= maxWidth && totalH <= maxHeight) {
      return { fontSize: size, lines, lineH, totalH };
    }
    size -= 2;
  }
  // Last-resort: text overflows even at minFontSize → return what we have
  // so the caller can paint (clipped) rather than crashing. Use a
  // hard-coded sans-serif fallback that exists on every platform.
  ctx.font = `${fontWeight} ${minFontSize}px sans-serif`;
  const lines = wrapText(ctx, text, maxWidth);
  const safeLines = lines.length > 0 ? lines : [text];
  const lineH = Math.round(minFontSize * lineGapRatio);
  return { fontSize: minFontSize, lines: safeLines, lineH, totalH: lineH * safeLines.length };
}

function hexToRgb(hex, fallback = [255, 68, 0]) {
  try {
    let h = (hex || "").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  } catch {
    return fallback;
  }
}

function rgbToCss(rgb) {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

// ============== single scene composition ==============

async function loadImage(srcDataUrl) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error("Image load failed"));
    img.src = srcDataUrl;
  });
}

function drawCoverImage(ctx, img, dw, dh) {
  // cover-fit (like backend's max-scale + center-crop)
  const scale = Math.max(dw / img.width, dh / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  const x = (dw - w) / 2;
  const y = (dh - h) / 2;
  ctx.drawImage(img, x, y, w, h);
}

/**
 * Compose a single image-scene frame (480×854) and return a JPEG Blob.
 * Replicates _compose_scene_frame from server.py.
 */
export async function composeSceneFrameBlob(scene, reel, options = {}) {
  // Render the scene's text + badges + CTA on a 480×854 canvas. When
  // `transparentBg=true`, the background image and the solid black fill
  // are skipped so the result can be used as a PNG overlay on top of a
  // user-uploaded video clip. The gradient bands (top + bottom) are kept
  // because they're what makes the white headline readable against a
  // bright/busy background.
  const { transparentBg = false } = options;
  const canvas = document.createElement("canvas");
  canvas.width = VIDEO_W;
  canvas.height = VIDEO_H;
  const ctx = canvas.getContext("2d", { alpha: transparentBg });

  // ---- background image ----
  if (!transparentBg) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, VIDEO_W, VIDEO_H);
    if (scene.image_base64) {
      try {
        const dataUrl = scene.image_base64.startsWith("data:")
          ? scene.image_base64
          : `data:image/jpeg;base64,${scene.image_base64}`;
        const img = await loadImage(dataUrl);
        drawCoverImage(ctx, img, VIDEO_W, VIDEO_H);
      } catch (_) {
        // black background fallback
      }
    }
  }

  // ---- top + bottom gradient overlays ----
  const topGrad = ctx.createLinearGradient(0, 0, 0, 300);
  topGrad.addColorStop(0, "rgba(0,0,0,0.47)");
  topGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = topGrad;
  ctx.fillRect(0, 0, VIDEO_W, 300);

  const botGrad = ctx.createLinearGradient(0, VIDEO_H - 600, 0, VIDEO_H);
  botGrad.addColorStop(0, "rgba(0,0,0,0)");
  botGrad.addColorStop(1, "rgba(0,0,0,0.78)");
  ctx.fillStyle = botGrad;
  ctx.fillRect(0, VIDEO_H - 600, VIDEO_W, 600);

  const brandRgb = hexToRgb(reel.brand_color_primary || "#FF4400");
  const brandCss = rgbToCss(brandRgb);

  // ---- top business badge ----
  const businessName = (reel.business_name || "").toUpperCase();
  if (businessName) {
    const badgeFontPx = 18; // backend renders 38px on 480 width — visually roughly equivalent at this scale
    ctx.font = `900 ${badgeFontPx}px Arial, "Helvetica Neue", sans-serif`;
    const badgeText = `  ${businessName}  `;
    const badgeW = ctx.measureText(badgeText).width;
    const badgeH = 30;
    const badgeX = 36;
    const badgeY = 46;
    ctx.fillStyle = brandCss;
    ctx.fillRect(badgeX, badgeY, badgeW + 10, badgeH);
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.fillText(badgeText, badgeX + 5, badgeY + badgeH / 2 + 1);
  }

  // ---- headline ----
  // The FIRST scene of every reel acts as a "title card": the reel title
  // is shown big as the headline and the hook (= the curiosity-driver
  // copy generated alongside the title) is shown as the subtitle. This
  // matches the conventions of high-converting Instagram/TikTok reels
  // where the first slide MUST hook the viewer before they swipe away.
  // HOWEVER — if the user has explicitly edited the scene's own
  // headline/subtitle (i.e. they're non-empty) we MUST respect that
  // edit. Otherwise customisations on the first slide get silently
  // overwritten by the auto-generated reel.title/hook every export.
  const isFirstScene = scene.scene_number === 1;
  const sceneHeadline = (scene.headline || "").trim();
  const sceneSubtitle = (scene.subtitle || "").trim();
  const rawHeadline = isFirstScene
    ? (sceneHeadline || reel.title || "")
    : sceneHeadline;
  const rawSubtitle = isFirstScene
    ? (sceneSubtitle || reel.hook || "")
    : sceneSubtitle;
  const headline = rawHeadline.toUpperCase();
  const textSize = scene.text_size || "normal";
  const headlineStartFont = textSize === "small" ? 36 : textSize === "large" ? 64 : 50;
  // Layout zones — values originally tuned for the 1080×1920 IG/TikTok
  // canvas, but THIS canvas is VIDEO_W×VIDEO_H (480×854 in the current
  // build). We derive the safe-zone constants proportionally so the
  // text always fits the actual frame.
  // Resolve "auto" to the band of the background that's darkest +
  // most uniform — that's where white text reads cleanest. We sample
  // the already-drawn canvas (post background + gradient overlays)
  // restricted to the headline-safe horizontal stripe. Cheap: one
  // getImageData on three small bands.
  let textPos = scene.text_position || "center";
  if (textPos === "auto") {
    try {
      const TOP_BAND_Y = Math.round(VIDEO_H * 0.057);
      const TOP_BAND_H = Math.round(VIDEO_H * 0.18);
      const MID_BAND_Y = Math.round(VIDEO_H * 0.35);
      const MID_BAND_H = Math.round(VIDEO_H * 0.20);
      const BOT_BAND_Y = Math.round(VIDEO_H * 0.62);
      const BOT_BAND_H = Math.round(VIDEO_H * 0.18);
      const sampleScore = (y, h) => {
        try {
          const img = ctx.getImageData(16, y, VIDEO_W - 32, h);
          const d = img.data;
          let sum = 0, sumSq = 0, n = 0;
          // Sub-sample every 4th pixel — 4x speedup, identical decision.
          for (let i = 0; i < d.length; i += 16) {
            const lum = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
            sum += lum;
            sumSq += lum * lum;
            n += 1;
          }
          const mean = sum / Math.max(1, n);
          const variance = sumSq / Math.max(1, n) - mean * mean;
          // Lower score = better for white text: dark mean + low variance.
          return mean * 1.0 + Math.sqrt(Math.max(0, variance)) * 0.6;
        } catch {
          return Infinity;
        }
      };
      const sTop = sampleScore(TOP_BAND_Y, TOP_BAND_H);
      const sMid = sampleScore(MID_BAND_Y, MID_BAND_H);
      const sBot = sampleScore(BOT_BAND_Y, BOT_BAND_H);
      const min = Math.min(sTop, sMid, sBot);
      textPos = sBot === min ? "bottom" : sTop === min ? "top" : "center";
    } catch {
      textPos = "center";
    }
  }
  const safeMaxW = VIDEO_W - 32;             // ~ 72px @ 1080 width
  const TOP_RESERVED = Math.round(VIDEO_H * 0.057);     // ~ 110px @ 1920 (= 5.7%)
  // CTA pushed slightly lower than before (was 82.3%) so the IG/TikTok
  // bottom UI overlay doesn't bite into it and the body text has more
  // room to breathe. The pill still sits well above the cropped area.
  // When `reel.show_cta === false`, the CTA pill is suppressed entirely
  // — useful for evergreen content where the brand handle alone is the
  // call-out. Existing reels default to true (show CTA).
  const showCta = reel.show_cta !== false;
  const CTA_TOP_Y_RATIO = showCta ? 0.875 : 0.95;       // 87.5% if shown, otherwise out of frame
  const CTA_TOP_Y = Math.round(VIDEO_H * CTA_TOP_Y_RATIO);
  const HEADLINE_TO_CTA_GAP = Math.round(VIDEO_H * 0.017); // ~ 32px @ 1920
  const BOTTOM_RESERVED = VIDEO_H - CTA_TOP_Y + HEADLINE_TO_CTA_GAP;
  const safeMaxH = textPos === "center"
    ? VIDEO_H - TOP_RESERVED - BOTTOM_RESERVED
    : Math.round(VIDEO_H * 0.24);              // ~ 460px @ 1920
  // Use Arial first (universally available across Windows/Android/iOS/macOS)
  // with sans-serif as a last-resort fallback. Helvetica Neue was preferred
  // visually but it's not bundled and missing on Android/Windows → would
  // cause measureText() to misbehave and lead to "no text in MP4".
  const fitFamily = 'Arial, "Helvetica Neue", Helvetica, sans-serif';
  const headlineFit = fitTextBlock(ctx, "900", fitFamily, headline, safeMaxW, safeMaxH, headlineStartFont, 22);
  const headlineFontPx = headlineFit.fontSize;
  const lines = headlineFit.lines;
  const lineH = headlineFit.lineH;
  const totalH = headlineFit.totalH;

  ctx.textBaseline = "alphabetic";
  ctx.font = `900 ${headlineFontPx}px ${fitFamily}`;

  let startY;
  // Pre-compute the worst-case subtitle block height so the "bottom"
  // branch can reserve EXACTLY the right amount of room (the previous
  // estimate clipped at 72px which was too tight for 3-line subtitles
  // at the default font size — the subtitle ended up tucked under the
  // CTA pill).
  const subHasText = !!(rawSubtitle && rawSubtitle.trim());
  const subAvailWorstCase = Math.round(VIDEO_H * 0.13); // ~111px @ 854, 3 lines × 24px line-height + breathing room
  const subReservedForBottom = subHasText ? subAvailWorstCase + 14 : 0;

  if (textPos === "top") startY = TOP_RESERVED;
  else if (textPos === "bottom") {
    // Lower edge of the headline block sits ABOVE: CTA - headline-to-cta gap - subtitle reservation.
    startY = CTA_TOP_Y - HEADLINE_TO_CTA_GAP - totalH - subReservedForBottom;
  }
  else startY = (VIDEO_H - totalH) / 2 - 80;
  // Clamp so the block never touches the top reserved area or the CTA.
  if (startY < TOP_RESERVED) startY = TOP_RESERVED;
  const headlineMaxY = CTA_TOP_Y - HEADLINE_TO_CTA_GAP - subReservedForBottom;
  if (startY + totalH > headlineMaxY) {
    startY = Math.max(TOP_RESERVED, headlineMaxY - totalH);
  }

  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    const tw = ctx.measureText(ln).width;
    const tx = (VIDEO_W - tw) / 2;
    const ty = startY + i * lineH + headlineFontPx;
    // shadow
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.fillText(ln, tx + 2, ty + 2);
    // text
    ctx.fillStyle = "#fff";
    ctx.fillText(ln, tx, ty);
  }

  // ---- subtitle ----
  if (rawSubtitle) {
    const subtitleText = rawSubtitle;
    // Subtitle sits just below the headline but always above the CTA.
    const subStart = startY + totalH + 14;
    const subAvailH = Math.max(40, CTA_TOP_Y - HEADLINE_TO_CTA_GAP - subStart);
    const subFit = fitTextBlock(ctx, "700", fitFamily, subtitleText, VIDEO_W - 90, subAvailH, 20, 14, 1.30);
    ctx.font = `700 ${subFit.fontSize}px ${fitFamily}`;
    let sy = subStart + subFit.fontSize;
    for (const ln of subFit.lines) {
      const tw = ctx.measureText(ln).width;
      const tx = (VIDEO_W - tw) / 2;
      ctx.fillStyle = "rgba(0,0,0,0.85)";
      ctx.fillText(ln, tx + 1, sy + 1);
      ctx.fillStyle = "#ebebeb";
      ctx.fillText(ln, tx, sy);
      sy += subFit.lineH;
    }
  }

  // ---- bottom CTA pill ----
  // Anchored ~87.5% from the top (raised from old 82.3% per user
  // request). When the user has explicitly hidden the CTA via the
  // toggle in the Reel detail page (`reel.show_cta === false`), we
  // skip drawing it entirely — backward compat: existing reels with no
  // `show_cta` field default to true (CTA shown).
  const cta = reel.cta || "";
  if (cta && showCta) {
    const ctaText = cta.toUpperCase();
    const innerPadX = 28;
    const maxRectW = VIDEO_W - 56;
    let ctaFontPx = null;
    let ctaLines = [ctaText];
    for (const size of [28, 24, 22, 20, 18]) {
      ctx.font = `900 ${size}px Arial, "Helvetica Neue", sans-serif`;
      if (ctx.measureText(ctaText).width <= maxRectW - 2 * innerPadX) {
        ctaFontPx = size;
        break;
      }
    }
    if (ctaFontPx === null) {
      ctaFontPx = 20;
      ctx.font = `900 ${ctaFontPx}px Arial, "Helvetica Neue", sans-serif`;
      ctaLines = wrapText(ctx, ctaText, maxRectW - 2 * innerPadX).slice(0, 2);
    }
    ctx.font = `900 ${ctaFontPx}px Arial, "Helvetica Neue", sans-serif`;
    const widths = ctaLines.map((ln) => ctx.measureText(ln).width);
    const maxLineW = Math.max(...widths);
    const lineHcta = Math.round(ctaFontPx * 1.15);
    const rectW = Math.min(Math.round(maxLineW) + 2 * innerPadX, maxRectW);
    const rectH = lineHcta * ctaLines.length + 22;
    const rectX = (VIDEO_W - rectW) / 2;
    const rectY = CTA_TOP_Y;

    // rounded rect
    const radius = 9;
    ctx.fillStyle = brandCss;
    ctx.beginPath();
    ctx.moveTo(rectX + radius, rectY);
    ctx.lineTo(rectX + rectW - radius, rectY);
    ctx.quadraticCurveTo(rectX + rectW, rectY, rectX + rectW, rectY + radius);
    ctx.lineTo(rectX + rectW, rectY + rectH - radius);
    ctx.quadraticCurveTo(rectX + rectW, rectY + rectH, rectX + rectW - radius, rectY + rectH);
    ctx.lineTo(rectX + radius, rectY + rectH);
    ctx.quadraticCurveTo(rectX, rectY + rectH, rectX, rectY + rectH - radius);
    ctx.lineTo(rectX, rectY + radius);
    ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "#fff";
    let ty = rectY + 12 + ctaFontPx;
    for (let i = 0; i < ctaLines.length; i++) {
      const ln = ctaLines[i];
      const tw = widths[i];
      const tx = (VIDEO_W - tw) / 2;
      ctx.fillText(ln, tx, ty);
      ty += lineHcta;
    }
  }

  // ---- export ----
  // PNG with alpha when used as a video overlay; otherwise JPEG (smaller,
  // and the canvas is fully opaque anyway).
  if (transparentBg) {
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Overlay encode failed"))),
        "image/png",
      );
    });
  }
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Frame encode failed"))),
      "image/jpeg",
      0.88,
    );
  });
}

// ============== fetch user video bytes ==============

async function fetchVideoBytes(videoId) {
  // Use the authenticated axios instance to hit /api/reels/uploads/video/{id}
  const res = await api.get(`/reels/uploads/video/${videoId}`, {
    responseType: "blob",
    timeout: 120000,
  });
  return new Uint8Array(await res.data.arrayBuffer());
}

async function fetchNarrationBytes(reelId) {
  // Pull the TTS MP3 generated server-side for this reel. Cache-bust with
  // a timestamp so a freshly-regenerated voice is never served stale.
  const res = await api.get(`/reels/${reelId}/narration/audio?t=${Date.now()}`, {
    responseType: "blob",
    timeout: 60000,
  });
  return new Uint8Array(await res.data.arrayBuffer());
}

// ============== main: render reel ==============

const SCENE_FALLBACK_DURATION_MS = 5000;
const MAX_VIDEO_DURATION_S = 30;

/**
 * Render a reel entirely client-side.
 * @param {Object} reel - the reel document (must contain scenes[], cta, brand_color_primary, business_name)
 * @param {Object} opts
 * @param {(pct:number, label?:string) => void} [opts.onProgress] - 0..100
 * @returns {Promise<Blob>} an MP4 Blob ready to download
 */
export async function renderReelClient(reel, { onProgress } = {}) {
  const scenes = reel.scenes || [];
  if (!scenes.length) throw new Error("Reel senza scene");

  const update = (pct, label) => {
    if (onProgress) onProgress(Math.min(99, Math.round(pct)), label);
  };

  // Ensure all browser fonts are loaded before we start measuring/painting.
  // Without this, the first scene can render with a fallback font that
  // measures 0-width → wrapText returns empty → no text in MP4.
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }

  update(2, "Caricamento motore video...");
  const ffmpeg = await getFFmpeg();

  // --- write all assets to ffmpeg FS ---
  const partFiles = [];
  // Reserve 40% of the progress bar for asset prep, 55% for ffmpeg encoding,
  // 5% for the final concat.
  for (let i = 0; i < scenes.length; i++) {
    const s = scenes[i];
    const baseProgress = 5 + Math.round((i / scenes.length) * 35);
    update(baseProgress, `Preparazione scena ${i + 1}/${scenes.length}...`);

    const durMs = Math.max(1000, Math.min(MAX_VIDEO_DURATION_S * 1000, s.duration_ms || SCENE_FALLBACK_DURATION_MS));
    const durS = (durMs / 1000).toFixed(2);

    if (s.video_id) {
      // download user video as raw bytes, write to FS
      const bytes = await fetchVideoBytes(s.video_id);
      const inName = `clip_${String(i).padStart(2, "0")}.mp4`;
      await ffmpeg.writeFile(inName, bytes);
      const outName = `part_${String(i).padStart(2, "0")}.ts`;

      // Render the same headline+CTA+badges card as image scenes, but
      // with a transparent background → ffmpeg overlays it on top of the
      // resized video frame so text appears ON the user's clip.
      const overlayBlob = await composeSceneFrameBlob(s, reel, { transparentBg: true });
      const overlayName = `txt_${String(i).padStart(2, "0")}.png`;
      await ffmpeg.writeFile(overlayName, await fetchFile(overlayBlob));

      const scaleAndPad = `[0:v]scale=${VIDEO_W}:${VIDEO_H}:force_original_aspect_ratio=decrease,pad=${VIDEO_W}:${VIDEO_H}:(ow-iw)/2:(oh-ih)/2:black,setsar=1,fps=24,format=yuv420p[base];[base][1:v]overlay=0:0:format=auto,format=yuv420p[v]`;
      // Try to keep the original audio track. If the source has no audio,
      // ffmpeg fails on the missing input — we then re-run with a silent
      // anullsrc track so every intermediate segment has identical stream
      // layout (concat-copy is strict about that).
      const baseAudioOutFlags = [
        "-c:a", "aac",
        "-ar", "44100",
        "-ac", "2",
        "-b:a", "96k",
      ];
      const baseVideoOutFlags = [
        "-c:v", "libx264",
        "-pix_fmt", "yuv420p",
        "-preset", "ultrafast",
        "-crf", "26",
      ];
      try {
        await ffmpeg.exec([
          "-y",
          "-ss", "0",
          "-t", durS,
          "-i", inName,
          "-i", overlayName,
          "-filter_complex", scaleAndPad,
          "-map", "[v]",
          "-map", "0:a:0",  // demand original audio — fails if missing
          ...baseVideoOutFlags,
          ...baseAudioOutFlags,
          "-shortest",
          "-bsf:v", "h264_mp4toannexb",
          "-f", "mpegts",
          outName,
        ]);
      } catch (_e) {
        // Source video had no audio track → re-run pairing the video with
        // an anullsrc silence generator so the segment still has audio.
        await ffmpeg.exec([
          "-y",
          "-ss", "0",
          "-t", durS,
          "-i", inName,
          "-i", overlayName,
          "-f", "lavfi",
          "-t", durS,
          "-i", "anullsrc=channel_layout=stereo:sample_rate=44100",
          "-filter_complex", scaleAndPad,
          "-map", "[v]",
          "-map", "2:a:0",
          ...baseVideoOutFlags,
          ...baseAudioOutFlags,
          "-shortest",
          "-bsf:v", "h264_mp4toannexb",
          "-f", "mpegts",
          outName,
        ]);
      }
      try { await ffmpeg.deleteFile(overlayName); } catch (_) {}
      // free the source file from FS
      try { await ffmpeg.deleteFile(inName); } catch (_) {}
      partFiles.push(outName);
    } else {
      // compose JPEG frame in canvas, write to FS
      const frameBlob = await composeSceneFrameBlob(s, reel);
      const inName = `frame_${String(i).padStart(2, "0")}.jpg`;
      await ffmpeg.writeFile(inName, await fetchFile(frameBlob));
      const outName = `part_${String(i).padStart(2, "0")}.ts`;
      const vf = `scale=${VIDEO_W}:${VIDEO_H}:force_original_aspect_ratio=decrease,pad=${VIDEO_W}:${VIDEO_H}:(ow-iw)/2:(oh-ih)/2:black,setsar=1,fps=24,format=yuv420p`;
      // Image scene: pair the still frame with a silent audio track so every
      // intermediate segment shares an identical AAC stereo audio stream
      // (otherwise concat-copy refuses to merge image-only segments with
      // video-with-audio segments).
      await ffmpeg.exec([
        "-y",
        "-loop", "1",
        "-t", durS,
        "-i", inName,
        "-f", "lavfi",
        "-t", durS,
        "-i", "anullsrc=channel_layout=stereo:sample_rate=44100",
        "-vf", vf,
        "-map", "0:v:0",
        "-map", "1:a:0",
        "-c:v", "libx264",
        "-pix_fmt", "yuv420p",
        "-preset", "ultrafast",
        "-crf", "26",
        "-c:a", "aac",
        "-ar", "44100",
        "-ac", "2",
        "-b:a", "96k",
        "-shortest",
        "-bsf:v", "h264_mp4toannexb",
        "-f", "mpegts",
        outName,
      ]);
      try { await ffmpeg.deleteFile(inName); } catch (_) {}
      partFiles.push(outName);
    }
  }

  // --- final concat (stream-copy, near-instant) ---
  update(95, "Unione finale...");
  const listText = partFiles.map((p) => `file '${p}'`).join("\n") + "\n";
  await ffmpeg.writeFile("list.txt", new TextEncoder().encode(listText));
  const concatName = "concat.mp4";
  await ffmpeg.exec([
    "-y",
    "-f", "concat",
    "-safe", "0",
    "-i", "list.txt",
    "-c", "copy",
    "-bsf:a", "aac_adtstoasc",
    "-movflags", "+faststart",
    concatName,
  ]);

  // --- Optional voiceover overlay ---
  // When the reel has a narration MP3 (TTS), we re-encode the audio track
  // with the narration mixed at a higher volume than the existing
  // (silent or original-video) audio. We keep the video stream-copied so
  // this second pass stays fast (~2-5s on most devices).
  let outName = concatName;
  let cleanupNarrationFiles = [];
  if (reel.narration_has_audio) {
    update(96, "Aggiunta voce narrante...");
    try {
      const audioBytes = await fetchNarrationBytes(reel.id);
      await ffmpeg.writeFile("voice.mp3", audioBytes);
      cleanupNarrationFiles.push("voice.mp3");

      // Compute the time ranges of video scenes — needed when mode is
      // "respect_videos" so we can mute the narration and boost the
      // original audio during those segments.
      const videoRanges = [];
      let cursor = 0;
      for (const s of scenes) {
        const dur = Math.max(1, Math.min(MAX_VIDEO_DURATION_S * 1000, s.duration_ms || SCENE_FALLBACK_DURATION_MS)) / 1000;
        if (s.video_id) {
          videoRanges.push([cursor, cursor + dur]);
        }
        cursor += dur;
      }

      const mode = reel.narration_mode || "overlay";
      let filterComplex;
      if (mode === "respect_videos" && videoRanges.length > 0) {
        // Build an "enable" expression that is truthy during ANY video
        // scene. `between(t,a,b)` returns 1 inside the range, 0 outside;
        // summing covers multiple ranges (clamped by FFmpeg implicitly).
        const between = videoRanges
          .map(([a, b]) => `between(t,${a.toFixed(3)},${b.toFixed(3)})`)
          .join("+");

        // ---- Lead silence: delay the voice past the LEADING video
        // segments. Without this, the voice plays muted under the
        // initial video and is already partway through its script when
        // the first image scene begins — that's the bug the user
        // reported. We compute the total duration of contiguous video
        // scenes at the start and `adelay` the voice by that amount.
        let leadingSilenceMs = 0;
        for (const s of scenes) {
          if (s.video_id) {
            const dur = Math.max(1, Math.min(MAX_VIDEO_DURATION_S * 1000, s.duration_ms || SCENE_FALLBACK_DURATION_MS));
            leadingSilenceMs += dur;
          } else {
            break;
          }
        }

        const delayPart = leadingSilenceMs > 0
          ? `adelay=${leadingSilenceMs}|${leadingSilenceMs},`
          : "";

        // bg: original/concat audio at full volume during video scenes,
        // ducked to 0.25 elsewhere.
        // voice: delayed past leading videos, then muted (0) during any
        // remaining video scenes, full (1) elsewhere.
        filterComplex =
          `[0:a:0]volume='if(${between},1.0,0.25)':eval=frame[bg];` +
          `[1:a:0]${delayPart}volume='if(${between},0.0,1.0)':eval=frame[vo];` +
          `[bg][vo]amix=inputs=2:duration=first:dropout_transition=0[aout]`;
      } else {
        // Default "overlay" mode: video audio always ducked to 25%, voice
        // always at 100%, mixed.
        filterComplex =
          `[0:a:0]volume=0.25[bg];[1:a:0]volume=1.0[vo];` +
          `[bg][vo]amix=inputs=2:duration=first:dropout_transition=0[aout]`;
      }

      const withVoice = "out.mp4";
      await ffmpeg.exec([
        "-y",
        "-i", concatName,
        "-i", "voice.mp3",
        // Keep the video as-is (stream-copy = no re-encode)
        "-map", "0:v:0",
        "-filter_complex", filterComplex,
        "-map", "[aout]",
        "-c:v", "copy",
        "-c:a", "aac",
        "-ar", "44100",
        "-ac", "2",
        "-b:a", "128k",
        "-shortest",
        "-movflags", "+faststart",
        withVoice,
      ]);
      outName = withVoice;
    } catch (e) {
      // If the narration overlay fails (network, decode, etc.) we still
      // ship the video without voice — the user will see a warning toast
      // from the caller and the rendered MP4 is fine.
      // eslint-disable-next-line no-console
      console.warn("Narration overlay failed, exporting without voice:", e);
      outName = concatName;
    }
  }

  // --- read result ---
  const data = await ffmpeg.readFile(outName);
  const blob = new Blob([data.buffer], { type: "video/mp4" });

  // --- cleanup ---
  try { await ffmpeg.deleteFile(outName); } catch (_) {}
  if (outName !== concatName) {
    try { await ffmpeg.deleteFile(concatName); } catch (_) {}
  }
  try { await ffmpeg.deleteFile("list.txt"); } catch (_) {}
  for (const p of partFiles) {
    try { await ffmpeg.deleteFile(p); } catch (_) {}
  }
  for (const p of cleanupNarrationFiles) {
    try { await ffmpeg.deleteFile(p); } catch (_) {}
  }

  update(100, "Fatto!");
  return blob;
}
